import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MapPinOff } from 'lucide-react'
import { api, ApiError } from '@/api/client'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { activateShowcase } from '@/lib/showcase-mode'

interface OAuthProvider {
  name: string
  authorize_url: string
  display_name?: string
}

interface GeoBlockState {
  country: string | null
}

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  github: 'GitHub',
  oidc: 'Single Sign-On',
}

const PROVIDER_ICONS: Record<string, JSX.Element> = {
  google: (
    <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  ),
  github: (
    <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
  ),
  oidc: (
    <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
}

export function LoginPage() {
  const [providers, setProviders] = useState<OAuthProvider[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [geoBlocked, setGeoBlocked] = useState<GeoBlockState | null>(null)
  const bufferRef = useRef('')

  // Hidden "demotime" keystroke listener for showcase mode activation
  useEffect(() => {
    const SECRET = 'demotime'
    const handler = (e: KeyboardEvent) => {
      if (e.key.length !== 1) return // ignore modifier keys
      bufferRef.current = (bufferRef.current + e.key).slice(-SECRET.length)
      if (bufferRef.current === SECRET) {
        bufferRef.current = ''
        activateShowcase()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  useEffect(() => {
    const loadProviders = async () => {
      try {
        const data = await api.getAuthProviders()
        setProviders(data.providers)
      } catch (e) {
        if (e instanceof ApiError && e.data?.error === 'geo_restricted') {
          setGeoBlocked({ country: (e.data.country as string) || null })
        } else {
          setError('Unable to load sign-in options. Is the API running?')
        }
      } finally {
        setLoading(false)
      }
    }
    loadProviders()
  }, [])

  const handleSignIn = (provider: OAuthProvider) => {
    // Navigate to the OmniAuth authorization URL directly on the API server
    const origin = api.apiOrigin
    window.location.href = `${origin}${provider.authorize_url}`
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
      >
        {/* Brand */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
              <path d="M8 10h8" />
              <path d="M8 14h4" />
            </svg>
          </div>
          <h1 className="text-xl tracking-tight"><span className="font-bold">Ledger.</span> <span className="font-light text-muted-foreground text-lg">Core</span></h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to continue</p>
        </div>

        <Card>
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <Spinner className="h-6 w-6 text-primary" />
              </div>
            ) : geoBlocked ? (
              <div className="py-4 text-center space-y-4">
                <MapPinOff className="mx-auto h-10 w-10 text-muted-foreground" />
                <div className="space-y-1">
                  <p className="text-sm font-medium">Ledger. Core isn't available in your country yet.</p>
                  <p className="text-xs text-muted-foreground">
                    Reach out to{' '}
                    <a href="mailto:hey@ledgerhq.nz" className="font-medium text-primary hover:underline">
                      hey@ledgerhq.nz
                    </a>{' '}
                    to find out when your country is expected to be on our roadmap.
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            ) : providers.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground py-4">
                No sign-in providers configured. Complete the{' '}
                <a href={`${api.apiOrigin}/setup`} className="font-medium text-primary hover:underline">first-run setup</a>{' '}
                to enable authentication.
              </div>
            ) : (
              <div className="space-y-3">
                {providers.map((provider) => (
                  <Button
                    key={provider.name}
                    variant="outline"
                    className="w-full justify-center py-5 text-sm"
                    onClick={() => handleSignIn(provider)}
                  >
                    {PROVIDER_ICONS[provider.name] || PROVIDER_ICONS.oidc}
                    Continue with {provider.display_name || PROVIDER_LABELS[provider.name] || provider.name}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Have an invitation?{' '}
          <Link to="/accept-invite" className="font-medium text-primary hover:underline">
            Accept it here
          </Link>
        </p>
      </motion.div>
    </div>
  )
}
